import React from 'react';
import { Shield, Lock, FileText, AlertCircle, X } from 'lucide-react';
import Button from '../../../components/ui/Button';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-xl font-heading font-semibold text-foreground">Datenschutzerklärung</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Schließen"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
        
        <div className="p-6 md:p-8 space-y-8">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-muted-foreground leading-relaxed">
              Mit dieser Datenschutzerklärung informieren wir Sie über die Verarbeitung personenbezogener Daten bei Nutzung unserer Website sowie im Rahmen der Registrierung und Vermarktung von THG-Quoten.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Stand: Januar 2026
            </p>
          </div>

          {/* Section 1: Verantwortlicher */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              1. Verantwortlicher
            </h3>
            <div className="space-y-2 text-muted-foreground">
              <p className="font-semibold text-foreground">Grid Balance UG</p>
              <p>79599 Wittlingen</p>
              <p>E-Mail: [bitte einfügen]</p>
              <p>Vertretungsberechtigt: Geschäftsführer</p>
            </div>
          </div>

          {/* Section 2: Verarbeitung personenbezogener Daten */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">
              2. Verarbeitung personenbezogener Daten
            </h3>
            <p className="text-muted-foreground mb-4">
              Wir verarbeiten personenbezogene Daten, die Sie uns im Rahmen der Registrierung zur Vermarktung von THG-Quoten oder beim Besuch unserer Website zur Verfügung stellen.
            </p>
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <p className="font-semibold text-foreground mb-2">Verarbeitete Daten (im Rahmen der THG-Quote):</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>Name, Anschrift, E-Mail-Adresse, Telefonnummer</li>
                <li>Fahrzeugscheine (Zulassungsbescheinigung Teil I)</li>
                <li>Halterdaten</li>
                <li>Ladepunktdaten (bei Unternehmen)</li>
                <li>Vertrags- und Abrechnungsdaten</li>
                <li>Firmenname, Ansprechpartner, Vertretungsberechtigungen (B2B)</li>
              </ul>
            </div>
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">Rechtsgrundlage:</span> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
            </p>
          </div>

          {/* Section 3: Weitergabe */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">
              3. Weitergabe an Vermarktungspartner und Behörden
            </h3>
            <p className="text-muted-foreground mb-4">
              Zur Abwicklung der THG-Quote geben wir Daten weiter an:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
              <li>Vermarktungspartner (z. B. Ecoturn GmbH)</li>
              <li>Umweltbundesamt (UBA) zur Quotenanerkennung</li>
              <li>Zahlungsdienstleister für Auszahlungen</li>
              <li>IT-Dienstleister (Hosting, Server)</li>
            </ul>
            <p className="text-muted-foreground">
              Die Weitergabe erfolgt ausschließlich zur Vertragserfüllung.
            </p>
          </div>

          {/* Section 4: Speicherung und Löschung */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">
              4. Speicherung und Löschung
            </h3>
            <p className="text-muted-foreground mb-4">
              Daten werden nur so lange gespeichert, wie dies zur Abwicklung notwendig ist oder gesetzliche Aufbewahrungspflichten bestehen.
            </p>
            <div className="bg-muted/50 rounded-lg p-4 mb-4">
              <p className="font-semibold text-foreground mb-2">Im Einzelnen:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>THG-Quotendaten: bis zu 3 Jahre (gesetzliche Anforderungen)</li>
                <li>Abrechnungsdaten: 10 Jahre (§ 147 AO)</li>
                <li>Website-Logfiles: max. 30 Tage</li>
              </ul>
            </div>
            <p className="text-muted-foreground">
              Danach werden die Daten gelöscht oder anonymisiert.
            </p>
          </div>

          {/* Section 5: Cookies & Tracking */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">
              5. Verwendung von Cookies & Tracking
            </h3>
            <p className="text-muted-foreground mb-4">
              Auf der Website können Cookies eingesetzt werden:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
              <li>technisch notwendige Cookies (Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO)</li>
              <li>optionale Cookies (Marketing, Analyse) nur nach Einwilligung (Art. 6 Abs. 1 lit. a DSGVO)</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Hinweis:</span> Bei Einsatz von Google Analytics, Meta Pixel oder LinkedIn Insights ist ein Cookie-Banner mit Opt-In erforderlich.
              </p>
            </div>
          </div>

          {/* Section 6: Ihre Rechte */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              6. Ihre Rechte als Betroffener
            </h3>
            <p className="text-muted-foreground mb-4">
              Sie haben folgende Rechte:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
              <li>Auskunft über gespeicherte Daten (Art. 15 DSGVO)</li>
              <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
              <li>Löschung (Art. 17 DSGVO), soweit keine Pflicht zur Aufbewahrung besteht</li>
              <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Widerspruch gegen Datenverarbeitung (Art. 21 DSGVO)</li>
              <li>Widerruf erteilter Einwilligungen (Art. 7 Abs. 3 DSGVO)</li>
            </ul>
            <p className="text-muted-foreground">
              Zur Ausübung kontaktieren Sie: <span className="font-semibold text-foreground">[bitte E-Mail-Adresse eintragen]</span>
            </p>
          </div>

          {/* Section 7: Datensicherheit */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">
              7. Datensicherheit
            </h3>
            <p className="text-muted-foreground">
              Wir sichern unsere Systeme durch technische und organisatorische Maßnahmen ab, um Ihre Daten gegen Verlust, Zugriff Unbefugter oder Missbrauch zu schützen. Dazu zählen verschlüsselte Übertragung (SSL), Zugriffskontrollen und serverseitige Sicherheitsmaßnahmen.
            </p>
          </div>

          {/* Section 8: Automatisierte Entscheidungsfindung */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">
              8. Automatisierte Entscheidungsfindung
            </h3>
            <p className="text-muted-foreground">
              Eine automatisierte Entscheidungsfindung oder ein Profiling findet nicht statt.
            </p>
          </div>

          {/* Section 9: Änderungen */}
          <div className="border-l-4 border-primary pl-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-primary" />
              9. Änderungen der Datenschutzerklärung
            </h3>
            <p className="text-muted-foreground">
              Wir behalten uns das Recht vor, diese Datenschutzerklärung anzupassen, um sie an geänderte Rechtslagen oder an neue technische Funktionen anzupassen.
            </p>
          </div>
        </div>
        
        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex items-center justify-end">
          <Button variant="default" onClick={onClose} iconName="">
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;